/* Google Maps JS calls window.gm_authFailure() when it rejects the API key
 * for this page (RefererNotAllowedMapError, InvalidKeyMapError, …). After
 * that, every DirectionsService / PlacesService / Geocoder request hangs
 * forever — Maps JS never sends them and never calls back. We record the
 * failure once so callers fail fast with the real reason, and log it to
 * Admin → Errors. Importing this module installs the watcher. */
import { reportClientError } from "./errorReporter";

let failed = false;
const waiters = new Set();

export function mapsKeyHint() {
  const k = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  return k ? `${k.slice(0, 10)}…${k.slice(-4)}` : "(no key configured)";
}

function siteOrigin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export function mapsAuthExplanation() {
  return (
    `Google rejected this site's Maps API key ${mapsKeyHint()} for ${siteOrigin()} ` +
    "(RefererNotAllowedMapError). Directions, Places and Geocoding will hang until it is fixed. " +
    `Fix: Google Cloud Console → APIs & Services → Credentials → that key → Website restrictions → add ${siteOrigin()}/* ` +
    "— or deploy with a key that already allows this domain — then reload."
  );
}

export function gmapsAuthFailed() {
  return failed;
}

/** Rejects (with the given error) the moment Google reports an auth failure. */
export function rejectOnGmapsAuthFailure(makeError) {
  return new Promise((_, reject) => {
    if (failed) {
      reject(makeError());
      return;
    }
    waiters.add(() => reject(makeError()));
  });
}

function onAuthFailure() {
  if (failed) return;
  failed = true;
  reportClientError(
    "maps_auth",
    `Google Maps rejected API key ${mapsKeyHint()} for ${siteOrigin()} — RefererNotAllowedMapError / InvalidKeyMapError`,
    {
      immediate: true,
      fingerprint: `fp_maps_auth_${siteOrigin()}`,
      context: { key_hint: mapsKeyHint(), origin: siteOrigin(), url: typeof window !== "undefined" ? window.location.pathname : "" },
    },
  );
  waiters.forEach((fn) => fn());
  waiters.clear();
}

export function installGmapsAuthWatcher() {
  if (typeof window === "undefined" || window.__gmapsAuthWatcher) return;
  window.__gmapsAuthWatcher = true;
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    onAuthFailure();
    if (typeof previous === "function") previous();
  };
}

/** Test hook. */
export function _resetGmapsAuthForTests() {
  failed = false;
  waiters.clear();
  if (typeof window !== "undefined") delete window.__gmapsAuthWatcher;
}

installGmapsAuthWatcher();
